/**
 * Metric Label Mapping Layer
 *
 * Translates solver-internal metric identifiers into plain-English labels
 * for non-expert users (grid operators, analysts, executives).
 *
 * Brand naming conventions (see skills/agents/blake/memory.md):
 *   "Enhanced UC Pipeline"  ->  "Generator Scheduling Engine"
 *   "MEDNS Polisher"        ->  "Schedule Refinement"
 *   "LP Probe"              ->  "Quick Classification"
 *   "ADMM"                  ->  "Zone Coordination"
 *   "Benders / gBD"         ->  "Monthly Solver"
 *   "Lagrangian Relaxation" ->  "Price-Based Coordinator"
 *   "Warm Start Injection"  ->  "Solution Reuse"
 *
 * Every metric exposed on a dashboard or tooltip MUST go through this
 * mapping so that end users never see raw solver jargon.
 */

// ============================================================
// TYPES
// ============================================================

/** Direction in which "better" values lie. */
export type GoodDirection = 'higher' | 'lower' | 'neutral';

/** Traffic-light status derived from thresholds. */
export type MetricStatus = 'good' | 'fair' | 'poor';

/** High-level category for grouping metrics in the UI. */
export type MetricCategory =
  | 'performance'
  | 'quality'
  | 'cost'
  | 'reliability';

/**
 * Complete label definition for a single solver metric.
 *
 * - `internalId`      — key used in solver result payloads (e.g. "lp_probe_fixing_rate")
 * - `userLabel`        — short label shown in dashboards (no jargon)
 * - `interpretation`   — one plain-English sentence explaining the metric
 * - `goodDirection`    — whether higher, lower, or neither is better
 * - `goodThreshold`    — value at or beyond which the metric is "good"
 * - `warningThreshold` — value at or beyond which the metric shifts to "poor"
 * - `unit`             — display unit string (e.g. "%", "MW", "$/MWh")
 * - `icon`             — Lucide icon name for use in UI components
 * - `category`         — grouping for dashboard sections
 */
export interface MetricLabel {
  internalId: string;
  userLabel: string;
  interpretation: string;
  goodDirection: GoodDirection;
  goodThreshold: number;
  warningThreshold: number;
  unit: string;
  icon: string;
  category: MetricCategory;
}

// ============================================================
// METRIC DEFINITIONS
// ============================================================

/**
 * Canonical list of solver metrics with user-facing translations.
 *
 * Thresholds are calibrated against IEEE 118 and RTS-GMLC test results
 * (see docs/plans/enhanced-uc/PRODUCTION_READINESS_REVIEW_2026_03_11.md).
 *
 * For "higher is better" metrics: value >= goodThreshold  -> good
 *                                  value >= warningThreshold -> fair
 *                                  value <  warningThreshold -> poor
 *
 * For "lower is better" metrics:  value <= goodThreshold  -> good
 *                                  value <= warningThreshold -> fair
 *                                  value >  warningThreshold -> poor
 */
export const METRIC_LABELS: readonly MetricLabel[] = [
  // ----- Performance -----
  {
    internalId: 'lp_probe_fixing_rate',
    userLabel: 'Pre-Solve Decision Confidence',
    interpretation:
      'Percentage of generator on/off decisions that the quick classification step resolved before the full solve.',
    goodDirection: 'higher',
    goodThreshold: 0.85,
    warningThreshold: 0.60,
    unit: '%',
    icon: 'Zap',
    category: 'performance',
  },
  {
    internalId: 'medns_binary_reduction',
    userLabel: 'Schedule Refinement Improvement',
    interpretation:
      'Fraction of remaining scheduling decisions resolved by the refinement step, reducing the work left for the main solver.',
    goodDirection: 'higher',
    goodThreshold: 0.70,
    warningThreshold: 0.40,
    unit: '%',
    icon: 'Sparkles',
    category: 'performance',
  },
  {
    internalId: 'solver_time_s',
    userLabel: 'Computation Time',
    interpretation:
      'Wall-clock time the solver took to find the optimal schedule.',
    goodDirection: 'lower',
    goodThreshold: 120,
    warningThreshold: 600,
    unit: 's',
    icon: 'Clock',
    category: 'performance',
  },
  {
    internalId: 'iteration_count',
    userLabel: 'Solver Iterations',
    interpretation:
      'Number of passes the solver made before converging on a solution.',
    goodDirection: 'lower',
    goodThreshold: 50,
    warningThreshold: 200,
    unit: '',
    icon: 'Repeat',
    category: 'performance',
  },
  {
    internalId: 'convergence_gap',
    userLabel: 'Solution Precision',
    interpretation:
      'How close the solution is to the mathematically proven best — lower means more precise.',
    goodDirection: 'lower',
    goodThreshold: 0.001,
    warningThreshold: 0.01,
    unit: '%',
    icon: 'Target',
    category: 'performance',
  },
  {
    internalId: 'admm_convergence_gap',
    userLabel: 'Zone Coordination Accuracy',
    interpretation:
      'How well the per-zone schedules agree with each other at zone boundaries after coordination.',
    goodDirection: 'lower',
    goodThreshold: 0.005,
    warningThreshold: 0.02,
    unit: '%',
    icon: 'GitMerge',
    category: 'performance',
  },
  {
    internalId: 'warmstart_speedup',
    userLabel: 'Solution Reuse Speedup',
    interpretation:
      'How much faster the solve was because it reused results from a previous run.',
    goodDirection: 'higher',
    goodThreshold: 1.15,
    warningThreshold: 1.0,
    unit: 'x',
    icon: 'FastForward',
    category: 'performance',
  },

  // ----- Quality -----
  {
    internalId: 'health_score',
    userLabel: 'Model Quality Score',
    interpretation:
      'Overall confidence in the optimization results, combining schedule accuracy, cost validity, and constraint satisfaction.',
    goodDirection: 'higher',
    goodThreshold: 0.90,
    warningThreshold: 0.70,
    unit: '',
    icon: 'HeartPulse',
    category: 'quality',
  },
  {
    internalId: 'load_balance_error_mw',
    userLabel: 'Supply-Demand Balance',
    interpretation:
      'Difference between total generation and total demand — should be near zero.',
    goodDirection: 'lower',
    goodThreshold: 0.1,
    warningThreshold: 1.0,
    unit: 'MW',
    icon: 'Scale',
    category: 'quality',
  },
  {
    internalId: 'commitment_accuracy',
    userLabel: 'Schedule Accuracy',
    interpretation:
      'How closely the computed on/off schedule matches the reference or expected schedule.',
    goodDirection: 'higher',
    goodThreshold: 0.90,
    warningThreshold: 0.75,
    unit: '%',
    icon: 'CheckCircle',
    category: 'quality',
  },
  {
    internalId: 'startup_mape',
    userLabel: 'Startup Prediction Error',
    interpretation:
      'Average error in predicting how many times generators start up, compared to reference data.',
    goodDirection: 'lower',
    goodThreshold: 0.10,
    warningThreshold: 0.20,
    unit: '%',
    icon: 'AlertTriangle',
    category: 'quality',
  },
  {
    internalId: 'binding_constraint_count',
    userLabel: 'Active System Limits',
    interpretation:
      'Number of physical or operational limits that are fully binding in the solution — more binding limits mean a tighter system.',
    goodDirection: 'neutral',
    goodThreshold: 0,
    warningThreshold: 0,
    unit: '',
    icon: 'Lock',
    category: 'quality',
  },

  // ----- Cost -----
  {
    internalId: 'objective_value',
    userLabel: 'Total System Cost',
    interpretation:
      'Total cost of operating the grid for the study period, including fuel, startup, and no-load costs.',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 0,
    unit: '$',
    icon: 'DollarSign',
    category: 'cost',
  },
  {
    internalId: 'congestion_cost',
    userLabel: 'Transmission Congestion Cost',
    interpretation:
      'Extra cost incurred because transmission lines are at capacity and cheaper generation cannot reach load.',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 0,
    unit: '$',
    icon: 'ArrowRightLeft',
    category: 'cost',
  },
  {
    internalId: 'lmp_range',
    userLabel: 'Price Spread',
    interpretation:
      'Difference between the highest and lowest locational prices across the network — a wide spread signals congestion.',
    goodDirection: 'neutral',
    goodThreshold: 0,
    warningThreshold: 0,
    unit: '$/MWh',
    icon: 'TrendingUp',
    category: 'cost',
  },
  {
    internalId: 'loss_factor',
    userLabel: 'Transmission Losses',
    interpretation:
      'Fraction of generated energy lost as heat in transmission lines.',
    goodDirection: 'lower',
    goodThreshold: 0.02,
    warningThreshold: 0.05,
    unit: '%',
    icon: 'Thermometer',
    category: 'cost',
  },
  {
    internalId: 'curtailment_mwh',
    userLabel: 'Renewable Energy Wasted',
    interpretation:
      'Amount of available wind or solar energy that was not used because the grid could not absorb it.',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 100,
    unit: 'MWh',
    icon: 'Wind',
    category: 'cost',
  },

  // ----- Reliability -----
  {
    internalId: 'ramp_violations',
    userLabel: 'Generator Ramp Violations',
    interpretation:
      'Number of hours where a generator was asked to change output faster than its physical ramp rate allows.',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 3,
    unit: '',
    icon: 'Activity',
    category: 'reliability',
  },
  {
    internalId: 'reserve_shortfall_mw',
    userLabel: 'Safety Reserve Shortfall',
    interpretation:
      'Amount of operating reserve below the required minimum — any shortfall means reduced ability to handle unexpected outages.',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 10,
    unit: 'MW',
    icon: 'Shield',
    category: 'reliability',
  },
  {
    internalId: 'isf_violation_count',
    userLabel: 'Transmission Limit Violations',
    interpretation:
      'Number of transmission lines where power flow exceeds the thermal rating.',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 3,
    unit: '',
    icon: 'AlertOctagon',
    category: 'reliability',
  },
  {
    internalId: 'n1_violation_count',
    userLabel: 'Contingency Violations',
    interpretation:
      'Number of post-contingency overloads found when simulating single-element outages (N-1 analysis).',
    goodDirection: 'lower',
    goodThreshold: 0,
    warningThreshold: 5,
    unit: '',
    icon: 'ShieldAlert',
    category: 'reliability',
  },
] as const;

// ============================================================
// LOOKUP INDEX  (built once at module load)
// ============================================================

const metricIndex: ReadonlyMap<string, MetricLabel> = new Map(
  METRIC_LABELS.map((m) => [m.internalId, m]),
);

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Look up the full label definition for a solver metric.
 *
 * @param metricId  The solver-internal metric key (e.g. "lp_probe_fixing_rate").
 * @returns The label definition, or `undefined` if the metric is not mapped.
 */
export function getMetricLabel(metricId: string): MetricLabel | undefined {
  return metricIndex.get(metricId);
}

/**
 * Evaluate a metric value against its thresholds and return a traffic-light status.
 *
 * - `'good'`  — value meets or exceeds the good threshold
 * - `'fair'`  — value is between warning and good thresholds
 * - `'poor'`  — value is beyond the warning threshold
 *
 * Neutral metrics (e.g. binding constraint count, LMP range) always return `'good'`
 * because there is no inherently better direction.
 *
 * @param metricId  The solver-internal metric key.
 * @param value     The numeric value to evaluate.
 * @returns Traffic-light status, or `'fair'` if the metric is unknown.
 */
export function getMetricStatus(metricId: string, value: number): MetricStatus {
  const label = metricIndex.get(metricId);
  if (!label) {
    return 'fair';
  }

  if (label.goodDirection === 'neutral') {
    return 'good';
  }

  if (label.goodDirection === 'higher') {
    if (value >= label.goodThreshold) return 'good';
    if (value >= label.warningThreshold) return 'fair';
    return 'poor';
  }

  // lower is better
  if (value <= label.goodThreshold) return 'good';
  if (value <= label.warningThreshold) return 'fair';
  return 'poor';
}

/**
 * Format a raw metric value with its unit for display.
 *
 * Examples:
 *   formatMetricValue('solver_time_s', 42.7)          -> "42.7 s"
 *   formatMetricValue('lp_probe_fixing_rate', 0.873)   -> "87.3%"
 *   formatMetricValue('objective_value', 1234567.89)    -> "$1,234,568"
 *   formatMetricValue('iteration_count', 84)            -> "84"
 *
 * @param metricId  The solver-internal metric key.
 * @param value     The raw numeric value from the solver.
 * @returns A human-readable string. Falls back to `value.toString()` for unknown metrics.
 */
export function formatMetricValue(metricId: string, value: number): string {
  const label = metricIndex.get(metricId);
  if (!label) {
    return value.toString();
  }

  const { unit } = label;

  // Percentage metrics: solver stores 0-1 fractions, display as 0-100%.
  if (unit === '%') {
    const pct = value <= 1 && value >= 0 ? value * 100 : value;
    return `${pct.toFixed(1)}%`;
  }

  // Dollar metrics: locale-formatted with $ prefix.
  if (unit === '$') {
    return `$${Math.round(value).toLocaleString('en-US')}`;
  }

  // $/MWh: two decimal places.
  if (unit === '$/MWh') {
    return `$${value.toFixed(2)}/MWh`;
  }

  // MW / MWh: one decimal place.
  if (unit === 'MW' || unit === 'MWh') {
    return `${value.toFixed(1)} ${unit}`;
  }

  // Seconds: one decimal place.
  if (unit === 's') {
    return `${value.toFixed(1)} s`;
  }

  // Multiplier (e.g. "1.23x").
  if (unit === 'x') {
    return `${value.toFixed(2)}x`;
  }

  // Unitless integers (iteration count, violation counts).
  if (unit === '') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  return `${value} ${unit}`;
}

/**
 * Build a full tooltip string for a metric, including label, formatted value,
 * status indicator, and plain-English interpretation.
 *
 * Example output:
 *   "Pre-Solve Decision Confidence: 87.3% (Good)
 *    Percentage of generator on/off decisions that the quick classification
 *    step resolved before the full solve."
 *
 * @param metricId  The solver-internal metric key.
 * @param value     The raw numeric value from the solver.
 * @returns Multi-line tooltip text. Falls back to a minimal string for unknown metrics.
 */
export function getMetricTooltip(metricId: string, value: number): string {
  const label = metricIndex.get(metricId);
  if (!label) {
    return `${metricId}: ${value}`;
  }

  const formatted = formatMetricValue(metricId, value);
  const status = getMetricStatus(metricId, value);
  const statusLabel =
    status === 'good' ? 'Good' : status === 'fair' ? 'Fair' : 'Needs Attention';

  return `${label.userLabel}: ${formatted} (${statusLabel})\n${label.interpretation}`;
}

/**
 * Return all metrics in a given category, preserving definition order.
 *
 * Useful for rendering grouped dashboard panels.
 *
 * @param category  The category to filter by.
 * @returns Array of matching MetricLabel objects.
 */
export function getMetricsByCategory(
  category: MetricCategory,
): readonly MetricLabel[] {
  return METRIC_LABELS.filter((m) => m.category === category);
}

/**
 * Return all known metric internal IDs.
 *
 * Useful for iterating over the complete metric registry.
 */
export function getAllMetricIds(): readonly string[] {
  return METRIC_LABELS.map((m) => m.internalId);
}
